/**
 * Modo preview de guías. Pinta una guía con los MISMOS renderers que el
 * runtime real, pero SIN resolver, cache de frecuencia, estado de
 * walkthrough ni tracking de interacciones. Pensado para el builder
 * no-code del dashboard: ver cómo queda una guía antes de publicarla.
 *
 * No requiere un `VeoClient` ni apiKey — es una función pura sobre el DOM.
 * Solo puede haber UNA preview activa a la vez; abrir otra cierra la
 * anterior (igual que el dashboard al cambiar de guía o de step).
 *
 * Diferencias de comportamiento vs. runtime real (deliberadas):
 *   - Las interacciones NO se reportan al backend.
 *   - `displayFrequency`/`activationRules` (url/trigger/delay) se ignoran:
 *     la preview siempre pinta de inmediato. Solo se respeta el `selector`
 *     del step (o el `activationRules.selector` como fallback) para anclar
 *     tooltips/walkthroughs.
 *   - CTA con `ctaAction: 'url'` sigue abriendo la URL en una pestaña nueva
 *     (igual que en producción) para poder probar el enlace.
 */
import { hasDocument } from '../../utils/safe-env';
import { BadgeRenderer } from './renderers/badge-renderer';
import { BannerRenderer } from './renderers/banner-renderer';
import { BaseRenderer } from './renderers/base-renderer';
import { CompositeRenderer } from './renderers/composite-renderer';
import { CustomRenderer } from './renderers/custom-renderer';
import { FormRenderer } from './renderers/form-renderer';
import { InlineCustomRenderer } from './renderers/inline-custom-renderer';
import { InlineFormRenderer } from './renderers/inline-form-renderer';
import { InlineRenderer } from './renderers/inline-renderer';
import { ModalRenderer } from './renderers/modal-renderer';
import { TooltipRenderer } from './renderers/tooltip-renderer';
import { WalkthroughRenderer } from './renderers/walkthrough-renderer';
import type {
  ActivationRules,
  GuideInteractionEvent,
  GuideStep,
  GuideType,
  ResolvedGuide,
  WalkthroughCallbacks,
} from './types';

/** Entrada mínima para previsualizar una guía. */
export interface PreviewGuideInput {
  guideType: GuideType;
  guideSteps: GuideStep[];
  /**
   * Opcional. Solo se usa `activationRules.selector` como fallback del
   * ancla para tooltip/walkthrough cuando el step no trae su propio
   * `selector`. El resto de reglas (url/trigger/delay) se ignoran.
   */
  activationRules?: Partial<ActivationRules>;
  /** Nombre mostrado (p.ej. en el `title` del iframe de guías `custom`). */
  guideName?: string;
  /**
   * Solo walkthrough: paso inicial a mostrar (default 0). Lo usa el editor del
   * dashboard para que la preview salte al paso que se está editando.
   */
  startStepIndex?: number;
  /**
   * Solo guías form: envío REAL de respuestas. Sin esto, el submit en preview
   * es un no-op (no escribe props). Lo usa el modo preview-por-link, que sí
   * guarda en el usuario que responde.
   */
  formSubmit?: (answers: Record<string, unknown>) => Promise<boolean>;
  /** Nota bajo el "¡Gracias!" cuando hay `formSubmit` propio. */
  formSubmitNote?: string;
  /** Modo badge: montar con el tooltip ABIERTO (toggle del editor). */
  badgeOpen?: boolean;
  /**
   * Se llama cuando el USUARIO cierra la preview (✕ / descartar), no cuando el
   * editor la reemplaza por otra. El modo builder lo usa para re-armar el
   * disparador por elemento objetivo.
   */
  onClosed?: () => void;
}

/** Resultado del primer render de una preview. */
export interface PreviewResult {
  /**
   * `false` cuando la guía no pudo pintarse — típicamente un
   * tooltip/walkthrough cuyo `selector` no existe en la página de preview.
   */
  rendered: boolean;
}

/** Handle de una preview activa. */
export interface PreviewHandle {
  /** Cierra y limpia la preview. Idempotente. */
  close(): void;
  /** Resuelve cuando el primer render terminó. */
  ready: Promise<PreviewResult>;
  /**
   * Host de la guía activa (o `null` si no hay nada montado). Lo usa el modo
   * builder para adjuntar la manipulación directa (drag/resize) del editor.
   */
  host(): HTMLElement | null;
}

const PREVIEW_GUIDE_ID = '__veo_preview__';

/**
 * Orquesta una única preview activa. A diferencia del `GuidesController`,
 * no toca queue, cache ni estado persistido: instancia el renderer y lo
 * monta directo, y maneja localmente la navegación de walkthroughs.
 */
class GuidePreviewController {
  private singleStep: BaseRenderer | null = null;
  /** Renderer del paso de walkthrough activo (walkthrough tooltip o modal/banner). */
  private walkthrough: { destroy(): void } | null = null;
  private walkthroughGuide: ResolvedGuide | null = null;
  private walkthroughIndex = 0;
  /** Input de la preview ACTIVA (para formSubmit/nota de forms). */
  private input: PreviewGuideInput | null = null;

  preview(input: PreviewGuideInput): PreviewHandle {
    // Update IN-PLACE: si el tipo no cambió y el renderer activo puede
    // actualizar su contenido (mismo ancla), evitamos remontar — el preview
    // del editor deja de parpadear en cada tecla.
    const nextStep = input.guideSteps[0];
    if (
      this.singleStep &&
      this.input &&
      input.guideType === this.input.guideType &&
      input.guideType !== 'walkthrough' &&
      // Abrir/cerrar el tooltip del badge requiere remontar.
      (input.badgeOpen === true) === (this.input.badgeOpen === true) &&
      nextStep &&
      this.singleStep.updateStep(nextStep)
    ) {
      this.input = input;
      return {
        close: () => this.close(),
        ready: Promise.resolve({ rendered: true }),
        host: () => this.activeHost(),
      };
    }

    this.close(); // solo una preview a la vez
    this.input = input;

    const guide = normalize(input);
    const start = typeof input.startStepIndex === 'number' ? input.startStepIndex : 0;
    // Composite: la preview muestra el PASO ACTIVO (el que se edita) como paso
    // único; el CompositeRenderer lee guideSteps[0].
    if (guide.guideType === 'composite') {
      const s = Math.min(Math.max(0, start), guide.guideSteps.length - 1);
      const active = guide.guideSteps[s];
      if (active) guide.guideSteps = [active];
      const ready = this.renderSingleStep(guide);
      return { close: () => this.close(), ready, host: () => this.activeHost() };
    }
    const ready =
      guide.guideType === 'walkthrough'
        ? this.startWalkthrough(guide, start)
        : this.renderSingleStep(guide);

    return { close: () => this.close(), ready, host: () => this.activeHost() };
  }

  /** Host de la guía montada (single-step o paso de walkthrough activo). */
  activeHost(): HTMLElement | null {
    if (this.singleStep) return this.singleStep.hostElement();
    if (this.walkthrough instanceof BaseRenderer) {
      return this.walkthrough.hostElement();
    }
    return null;
  }

  close(byUser = false): void {
    const onClosed = byUser ? this.input?.onClosed : undefined;
    if (this.singleStep) {
      safeDestroy(this.singleStep);
      this.singleStep = null;
    }
    if (this.walkthrough) {
      safeDestroy(this.walkthrough);
      this.walkthrough = null;
    }
    this.walkthroughGuide = null;
    this.walkthroughIndex = 0;
    this.input = null;
    onClosed?.();
  }

  private async renderSingleStep(guide: ResolvedGuide): Promise<PreviewResult> {
    const renderer = createSingleStepRenderer(guide.guideType);
    if (!renderer) return { rendered: false };
    this.singleStep = renderer;

    // Los renderers single-step solo emiten `shown` tras montar con éxito
    // (tooltip/custom esperan a su ancla). Lo usamos como señal de render.
    let shown = false;
    try {
      await renderer.render({
        guide,
        onInteraction: (event: GuideInteractionEvent) => {
          if (event.action === 'shown') shown = true;
        },
        onClose: () => this.close(true),
        badgeOpen: this.input?.badgeOpen === true,
        onTrack: () => {},
        // Encadenado en preview: no-op — el editor no puede montar otra guía
        // guardada; el click igual cierra la preview actual (vía onCtaClick).
        onOpenGuide: () => {},
        // Preview de forms: por defecto simula el envío SIN escribir props;
        // el caller puede pasar `formSubmit` real (link de preview).
        onFormSubmit: this.input?.formSubmit ?? (() => Promise.resolve(true)),
        isPreview: true,
        ...(this.input?.formSubmit && this.input.formSubmitNote !== undefined
          ? { previewNote: this.input.formSubmitNote }
          : {}),
      });
    } catch {
      this.close();
      return { rendered: false };
    }

    // El usuario pudo haber cerrado/reemplazado la preview mientras el render
    // esperaba el ancla (los renderers async como inline/inline-custom hacen
    // `await waitForElement` ANTES de insertar su host). En ese caso este
    // renderer quedó HUÉRFANO: su host ya entró al DOM pero `singleStep` apunta
    // a otro, así que el `close()` que lo reemplazó no lo borró. Lo destruimos
    // aquí para no apilar copias fantasma (causa de la guía duplicada).
    if (this.singleStep !== renderer) {
      safeDestroy(renderer);
      return { rendered: false };
    }

    return { rendered: shown };
  }

  private async startWalkthrough(guide: ResolvedGuide, startIndex = 0): Promise<PreviewResult> {
    if (guide.guideSteps.length === 0) return { rendered: false };
    this.walkthroughGuide = guide;
    const idx = Math.min(Math.max(0, Math.floor(startIndex)), guide.guideSteps.length - 1);
    this.walkthroughIndex = idx;
    const rendered = await this.renderWalkthroughStep(idx);
    return { rendered };
  }

  private async renderWalkthroughStep(index: number): Promise<boolean> {
    const guide = this.walkthroughGuide;
    if (!guide) return false;

    if (this.walkthrough) {
      safeDestroy(this.walkthrough);
      this.walkthrough = null;
    }

    const callbacks: WalkthroughCallbacks = {
      onNext: () => {
        const next = this.walkthroughIndex + 1;
        if (next >= guide.guideSteps.length) {
          this.close();
          return;
        }
        this.walkthroughIndex = next;
        void this.renderWalkthroughStep(next);
      },
      onBack: () => {
        const prev = this.walkthroughIndex - 1;
        if (prev < 0) return;
        this.walkthroughIndex = prev;
        void this.renderWalkthroughStep(prev);
      },
      onSkip: () => this.close(),
      onComplete: () => this.close(),
    };

    // Walkthrough heterogéneo: el paso puede pedir modal/banner (con navegación).
    const step = guide.guideSteps[index];
    const render = (step?.style as Record<string, unknown> | null | undefined)?.render;
    if (render === 'modal' || render === 'banner') {
      const renderer = render === 'modal' ? new ModalRenderer() : new BannerRenderer();
      try {
        renderer.render({
          guide,
          onInteraction: () => {},
          onClose: () => this.close(true),
          nav: { stepIndex: index, totalSteps: guide.guideSteps.length, callbacks },
        });
      } catch {
        safeDestroy(renderer);
        return false;
      }
      if (this.walkthroughGuide !== guide) {
        safeDestroy(renderer);
        return false;
      }
      this.walkthrough = renderer;
      return true;
    }

    const renderer = new WalkthroughRenderer();
    let success = false;
    try {
      success = await renderer.render({ guide, currentStepIndex: index, callbacks });
    } catch {
      success = false;
    }

    // Misma carrera que en single-step: si otra preview reemplazó esta
    // mientras esperábamos, no adoptamos el renderer (sería huérfano).
    if (!success || this.walkthroughGuide !== guide) {
      safeDestroy(renderer);
      return false;
    }
    this.walkthrough = renderer;
    return true;
  }
}

let activeController: GuidePreviewController | null = null;

/**
 * Previsualiza una guía en la página actual usando los renderers reales
 * del SDK. Devuelve un handle con `close()` y una promesa `ready`.
 *
 * Pensado para el builder no-code: no necesita `init()` ni apiKey. Abrir
 * una nueva preview cierra automáticamente la anterior.
 *
 * @example
 * ```ts
 * import { previewGuide } from '@veo/sdk';
 *
 * const preview = previewGuide({
 *   guideType: 'modal',
 *   guideSteps: [{ title: 'Hola', content: 'Esto es una preview' }],
 * });
 * const { rendered } = await preview.ready;
 * // ...más tarde:
 * preview.close();
 * ```
 */
export function previewGuide(input: PreviewGuideInput): PreviewHandle {
  if (!hasDocument()) {
    return { close: () => {}, ready: Promise.resolve({ rendered: false }), host: () => null };
  }
  if (!activeController) activeController = new GuidePreviewController();
  return activeController.preview(input);
}

/** Cierra la preview activa, si la hay. Idempotente. */
export function closeGuidePreview(): void {
  activeController?.close();
}

/** Construye un `ResolvedGuide` sintético con defaults inocuos para preview. */
function normalize(input: PreviewGuideInput): ResolvedGuide {
  const selector = input.activationRules?.selector;
  const activationRules: ActivationRules = {
    url: input.activationRules?.url ?? { type: 'prefix', pattern: '/' },
    trigger: 'immediate',
    ...(selector !== undefined ? { selector } : {}),
  };

  return {
    guideId: PREVIEW_GUIDE_ID,
    guideName: input.guideName ?? 'Preview',
    guideType: input.guideType,
    guideSteps: input.guideSteps,
    activationRules,
    displayFrequency: 'always',
    displayPriority: 0,
  };
}

function createSingleStepRenderer(type: GuideType): BaseRenderer | null {
  switch (type) {
    case 'modal':
      return new ModalRenderer();
    case 'banner':
      return new BannerRenderer();
    case 'tooltip':
      return new TooltipRenderer();
    case 'custom':
      return new CustomRenderer();
    case 'inline':
      return new InlineRenderer();
    case 'inline-custom':
      return new InlineCustomRenderer();
    case 'form':
      return new FormRenderer();
    case 'inline-form':
      return new InlineFormRenderer();
    case 'badge':
      return new BadgeRenderer();
    case 'composite':
      return new CompositeRenderer();
    case 'walkthrough':
      return null;
    default:
      return null;
  }
}

function safeDestroy(renderer: { destroy(): void }): void {
  try {
    renderer.destroy();
  } catch {
    // swallow — un cleanup roto no debe romper la preview.
  }
}
